import type { IEmailLogRepository, EmailLog, EmailLogStats } from '../interfaces/IEmailLogRepository';
import { simulateDelay, maybeSimulateError } from './mockData';

const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

const mockEmailLogs: EmailLog[] = [
  {
    id: 'log-1',
    hub_template_id: 1,
    template_name: 'Standard Reminder',
    recipient_email: 'john@acme.com',
    recipient_name: 'John Smith',
    subject: 'Action Required: Complete Your Acme Corporation Onboarding',
    body: 'Hello John, we noticed...',
    status: 'opened',
    sent_at: daysAgo(2),
    delivered_at: daysAgo(2),
    opened_at: daysAgo(1),
    sent_by: 'admin-1',
    related_entity_type: 'onboarding_flow',
    related_entity_id: 'flow-1',
  },
  {
    id: 'log-2',
    hub_template_id: 2,
    template_name: 'Urgent Escalation',
    recipient_email: 'mike@techstart.com',
    recipient_name: 'Mike Wilson',
    subject: 'URGENT: TechStart Ltd Onboarding Requires Immediate Attention',
    body: 'Dear Mike, this is urgent...',
    status: 'delivered',
    sent_at: daysAgo(5),
    delivered_at: daysAgo(5),
    sent_by: 'admin-1',
    related_entity_type: 'onboarding_flow',
    related_entity_id: 'flow-2',
  },
  {
    id: 'log-3',
    hub_template_id: 1,
    template_name: 'Standard Reminder',
    recipient_email: 'sarah@global.com',
    recipient_name: 'Sarah Johnson',
    subject: 'Action Required: Complete Your Global Media Inc Onboarding',
    body: 'Hello Sarah...',
    status: 'bounced',
    sent_at: daysAgo(7),
    bounce_reason: 'Mailbox full',
    sent_by: 'admin-2',
    related_entity_type: 'onboarding_flow',
    related_entity_id: 'flow-3',
  },
  {
    id: 'log-4',
    hub_template_id: 3,
    template_name: 'Welcome Email',
    recipient_email: 'alex@creative.com',
    recipient_name: 'Alex Brown',
    subject: 'Welcome to Creative Agency Co!',
    body: 'Welcome Alex...',
    status: 'clicked',
    sent_at: daysAgo(1),
    delivered_at: daysAgo(1),
    opened_at: daysAgo(1),
    clicked_at: daysAgo(1),
    sent_by: 'admin-1',
    related_entity_type: 'onboarding_flow',
    related_entity_id: 'flow-4',
  },
  {
    id: 'log-5',
    hub_template_id: 1,
    template_name: 'Standard Reminder',
    recipient_email: 'invalid@nonexistent.com',
    subject: 'Action Required',
    body: 'Hello...',
    status: 'failed',
    sent_at: daysAgo(3),
    error_message: 'SMTP connection timeout',
    sent_by: 'admin-1',
  },
];

export class MockEmailLogRepository implements IEmailLogRepository {
  private logs: EmailLog[] = [...mockEmailLogs];

  async getAll(filters?: {
    status?: EmailLog['status'];
    recipient?: string;
    hub_template_id?: number;
    related_entity_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<{ logs: EmailLog[]; total: number }> {
    await simulateDelay();
    maybeSimulateError();

    let filtered = [...this.logs];

    if (filters?.status) {
      filtered = filtered.filter(l => l.status === filters.status);
    }
    if (filters?.recipient) {
      filtered = filtered.filter(l => 
        l.recipient_email.toLowerCase().includes(filters.recipient!.toLowerCase())
      );
    }
    if (filters?.hub_template_id) {
      filtered = filtered.filter(l => l.hub_template_id === filters.hub_template_id);
    }
    if (filters?.related_entity_id) {
      filtered = filtered.filter(l => l.related_entity_id === filters.related_entity_id);
    }
    if (filters?.start_date) {
      filtered = filtered.filter(l => new Date(l.sent_at) >= new Date(filters.start_date!));
    }
    if (filters?.end_date) {
      filtered = filtered.filter(l => new Date(l.sent_at) <= new Date(filters.end_date!));
    }

    const total = filtered.length;

    // Sort by sent_at descending
    filtered.sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());

    // Apply pagination
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);

    return { logs: paginated, total };
  }

  async getById(id: string): Promise<EmailLog | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.logs.find(l => l.id === id) || null;
  }

  async getByRecipient(email: string): Promise<EmailLog[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.logs.filter(l => l.recipient_email === email);
  }

  async getByTemplate(templateId: number): Promise<EmailLog[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.logs.filter(l => l.hub_template_id === templateId);
  }

  async getStats(filters?: {
    start_date?: string;
    end_date?: string;
    hub_template_id?: number;
  }): Promise<EmailLogStats> {
    await simulateDelay();
    maybeSimulateError();

    let filtered = [...this.logs];

    if (filters?.start_date) {
      filtered = filtered.filter(l => new Date(l.sent_at) >= new Date(filters.start_date!));
    }
    if (filters?.end_date) {
      filtered = filtered.filter(l => new Date(l.sent_at) <= new Date(filters.end_date!));
    }
    if (filters?.hub_template_id) {
      filtered = filtered.filter(l => l.hub_template_id === filters.hub_template_id);
    }

    const total_sent = filtered.length;
    const total_delivered = filtered.filter(l => 
      ['delivered', 'opened', 'clicked'].includes(l.status)
    ).length;
    const total_bounced = filtered.filter(l => l.status === 'bounced').length;
    const total_opened = filtered.filter(l => 
      ['opened', 'clicked'].includes(l.status)
    ).length;
    const total_clicked = filtered.filter(l => l.status === 'clicked').length;

    return {
      total_sent,
      total_delivered,
      total_bounced,
      total_opened,
      total_clicked,
      delivery_rate: total_sent > 0 ? (total_delivered / total_sent) * 100 : 0,
      open_rate: total_delivered > 0 ? (total_opened / total_delivered) * 100 : 0,
      click_rate: total_opened > 0 ? (total_clicked / total_opened) * 100 : 0,
      bounce_rate: total_sent > 0 ? (total_bounced / total_sent) * 100 : 0,
    };
  }

  async create(data: {
    hub_template_id?: number;
    template_name: string;
    recipient_email: string;
    recipient_name?: string;
    subject: string;
    body: string;
    sent_by: string;
    related_entity_type?: EmailLog['related_entity_type'];
    related_entity_id?: string;
    metadata?: Record<string, any>;
  }): Promise<EmailLog> {
    await simulateDelay();
    maybeSimulateError();

    const log: EmailLog = {
      id: `log-${Date.now()}`,
      ...data,
      status: 'sent',
      sent_at: new Date().toISOString(),
    };

    this.logs.unshift(log);

    // Simulate automatic status updates
    setTimeout(async () => {
      await this.updateStatus(log.id, 'delivered');
    }, 2000);

    return log;
  }

  async updateStatus(
    id: string,
    status: EmailLog['status'],
    metadata?: {
      bounce_reason?: string;
      error_message?: string;
    }
  ): Promise<EmailLog> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.logs.findIndex(l => l.id === id);
    if (index === -1) throw new Error('Email log not found');

    const now = new Date().toISOString();
    const updates: Partial<EmailLog> = { status };

    if (status === 'delivered') {
      updates.delivered_at = now;
    } else if (status === 'opened') {
      updates.opened_at = now;
      if (!this.logs[index].delivered_at) {
        updates.delivered_at = now;
      }
    } else if (status === 'clicked') {
      updates.clicked_at = now;
      if (!this.logs[index].opened_at) {
        updates.opened_at = now;
      }
      if (!this.logs[index].delivered_at) {
        updates.delivered_at = now;
      }
    } else if (status === 'bounced') {
      updates.bounce_reason = metadata?.bounce_reason;
    } else if (status === 'failed') {
      updates.error_message = metadata?.error_message;
    }

    this.logs[index] = { ...this.logs[index], ...updates };
    return this.logs[index];
  }

  async getRecentActivity(limit: number = 10): Promise<EmailLog[]> {
    await simulateDelay();
    maybeSimulateError();
    
    return [...this.logs]
      .sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime())
      .slice(0, limit);
  }

  async getDeliveryTimeline(days: number = 7): Promise<Array<{ date: string; sent: number; delivered: number; opened: number }>> {
    await simulateDelay();
    maybeSimulateError();

    const timeline: Array<{ date: string; sent: number; delivered: number; opened: number }> = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayLogs = this.logs.filter(l => 
        l.sent_at.startsWith(dateStr)
      );

      timeline.push({
        date: dateStr,
        sent: dayLogs.length,
        delivered: dayLogs.filter(l => ['delivered', 'opened', 'clicked'].includes(l.status)).length,
        opened: dayLogs.filter(l => ['opened', 'clicked'].includes(l.status)).length,
      });
    }

    return timeline;
  }
}
